import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Briefcase,
  MapPin,
  ArrowRight,
  Users,
  Award,
  ShieldCheck,
  Handshake,
  TrendingUp,
  Quote,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Container, GlassCard } from "@/components/site/Primitives";
import { SectionRail } from "@/components/site/SectionRail";
import { stockImages } from "@/lib/content";
import { loadCareers, loadPageBySlug, loadTestimonials } from "@/lib/content-loader";
import { CraftRenderer } from "@/components/craft/CraftRenderer";
import { hasCraftContent } from "@/lib/craft/serialize";
import { useFirmContact } from "@/lib/hooks/use-firm-contact";
import careersHero from "@/assets/careers-team-hero.jpg.asset.json";

export const Route = createFileRoute("/careers")({
  loader: async () => ({
    jobs: await loadCareers(),
    cmsPage: await loadPageBySlug("careers"),
    testimonials: await loadTestimonials(),
  }),
  head: () => ({
    meta: [
      { title: "Careers — KS Lau & Co" },
      {
        name: "description",
        content:
          "Build a career with purpose at KS Lau & Co. Partner-led mentorship, diverse client exposure and a culture that invests in your growth.",
      },
      { property: "og:title", content: "Careers — KS Lau & Co" },
      {
        property: "og:description",
        content: "Join a partner-led chartered accountancy firm in Penang.",
      },
    ],
  }),
  component: CareersPage,
});

function careerApplyHref(job: { title: string; applyEmail?: string | null }) {
  const email = job.applyEmail?.trim() || "info@kslau.com.my";
  const subject = encodeURIComponent(`Application - ${job.title}`);
  return `mailto:${email}?subject=${subject}`;
}

const cultureValues = [
  {
    icon: Award,
    t: "Excellence",
    b: "We uphold the highest standards of professionalism, technical excellence and client service in every engagement.",
  },
  {
    icon: ShieldCheck,
    t: "Integrity",
    b: "We uphold the highest standards of ethics, honesty and accountability in everything we do.",
  },
  {
    icon: Handshake,
    t: "Collaboration",
    b: "We collaborate closely with our clients and colleagues, combining expertise and insights to deliver practical, value-driven solutions.",
  },
  {
    icon: Users,
    t: "People First",
    b: "We place people at the centre of everything we do, fostering a culture of respect, care and continuous growth so our people and clients can succeed together.",
  },
];

function CareersPage() {
  const { jobs, cmsPage, testimonials } = Route.useLoaderData();
  const firmContact = useFirmContact();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const current = testimonials[activeTestimonial] ?? testimonials[0];
  const primaryJob = jobs[0];

  if (cmsPage && hasCraftContent(cmsPage.craftState as string | object)) {
    return (
      <CraftRenderer
        craftState={cmsPage.craftState}
        craftCustomCss={cmsPage.craftCustomCss}
      />
    );
  }

  const applyHref = primaryJob?.applyEmail
    ? `mailto:${primaryJob.applyEmail}?subject=Application — ${encodeURIComponent(primaryJob.title)}`
    : `mailto:${firmContact.email}?subject=Application — Tax Associate`;
  const generalHref = `mailto:${firmContact.email}?subject=General Application — KS Lau %26 Co`;
  const cvHref = `mailto:${firmContact.email}?subject=Application — KS Lau %26 Co`;

  return (
    <>
      <SectionRail
        sections={[
          { id: "careers-culture", label: "Culture" },
          { id: "careers-opportunities", label: "Opportunities" },
          { id: "careers-life", label: "Life at KS Lau" },
          { id: "careers-cv", label: "Submit CV" },
        ]}
      />

      {/* Hero + Culture strip */}
      <section className="relative">
        {/* Hero band */}
        <div className="relative isolate overflow-hidden bg-primary pb-24 md:pb-32">
          {/* Right-side image */}
          <div className="absolute inset-y-0 right-0 -z-10 w-full md:w-3/5 lg:w-7/12">
            <img
              src={careersHero.url}
              alt="KS Lau & Co team collaborating"
              className="h-full w-full object-cover"
            />
            {/* Navy fade over image for text contrast */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent md:from-primary md:via-primary/40 md:to-transparent"
            />
          </div>

          <Container>
            <div className="flex min-h-[32vh] flex-col justify-center py-10 text-primary-foreground md:min-h-[40vh] md:py-14">
              <h1 className="max-w-2xl font-serif text-4xl font-normal leading-[1.1] tracking-tight md:text-5xl lg:text-6xl">
                Build Your Future.
                <br />
                <span className="text-white">Make an Impact.</span>
              </h1>
            </div>
          </Container>
        </div>

        {/* Culture strip — overlaps hero bottom */}
        <Container>
          <div
            id="careers-culture"
            className="relative z-10 -mt-20 scroll-mt-28 rounded-md bg-card p-6 shadow-xl md:-mt-24 md:p-8"
          >
            <div className="flex flex-wrap items-start gap-6 md:gap-5 lg:flex-nowrap lg:gap-6">
              <div className="w-full lg:w-auto lg:min-w-[140px] lg:max-w-[160px]">
                <h2 className="font-serif text-2xl font-normal tracking-tight text-primary md:text-3xl">
                  Our Culture
                </h2>
                <span aria-hidden="true" className="mt-3 block h-[2px] w-12 bg-gold" />
              </div>

              {cultureValues.map(({ icon: Icon, t, b }) => (
                <div key={t} className="flex min-w-[140px] flex-1 flex-col">
                  <Icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-3 font-serif text-base font-normal tracking-tight text-primary">
                    {t}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {b}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Explore Opportunities */}
      <section
        id="careers-opportunities"
        className="relative scroll-mt-28 pt-20 pb-20 md:pt-24 md:pb-28"
      >
        <Container>
          <div className="grid gap-10 md:grid-cols-[1fr_1.6fr] md:gap-14">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">
                Explore Opportunities
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
                We're always looking for talented and motivated individuals to join our
                team. Apply directly to an open role below.
              </p>
            </div>

            <GlassCard hover={false} className="overflow-hidden p-0">
              {jobs.map((job) => (
                <div
                  key={job.id ?? job.slug}
                  className="group flex flex-col gap-3 border-b border-primary/10 px-6 py-5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-8 sm:py-6"
                >
                  <Link
                    to="/careers/$slug"
                    params={{ slug: job.slug }}
                    className="min-w-0 flex-1 transition hover:opacity-90"
                  >
                    {job.slug === primaryJob?.slug ? (
                      <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-gold">
                        <Briefcase className="h-3.5 w-3.5" /> Now hiring
                      </p>
                    ) : null}
                    <h3 className="mt-2 text-lg font-semibold tracking-tight text-primary md:text-xl">
                      {job.title}
                    </h3>
                  </Link>
                  <p className="inline-flex items-center gap-1.5 text-base text-muted-foreground sm:w-32">
                    <MapPin className="h-4 w-4" /> {job.location ?? "Penang"}
                  </p>
                  <p className="text-base text-muted-foreground sm:w-28">{job.employmentType ?? "Full-time"}</p>
                  <a
                    href={careerApplyHref(job)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
                  >
                    Apply now <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              ))}
            </GlassCard>
          </div>

          <p className="mt-8 text-center text-base text-muted-foreground md:text-lg">
            Don't see the right role? We'd love to hear from you.{" "}
            <a
              href={generalHref}
              className="inline-flex items-center gap-1 font-semibold text-gold hover:underline"
            >
              Submit a General Application <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </p>
        </Container>
      </section>

      {/* Life at KS Lau — compact testimonial row */}
      <section
        id="careers-life"
        className="relative scroll-mt-28 bg-secondary/40 py-12 md:py-16"
      >
        <Container>
          <div className="grid items-stretch gap-8 md:grid-cols-12 md:gap-8">
            {/* Left: intro */}
            <div className="flex flex-col justify-center md:col-span-4 md:pr-2">
              <h2 className="font-serif text-2xl font-normal tracking-tight text-primary md:text-3xl">
                Life at KS Lau & Co
              </h2>
              <span aria-hidden="true" className="mt-3 block h-[2px] w-12 bg-gold" />
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                Hear from our people about their journey and what makes
                KS Lau & Co a great place to build a career.
              </p>
              <Link
                to="/about"
                hash="leadership"
                className="mt-5 inline-flex w-fit items-center justify-center rounded-md border border-primary bg-transparent px-5 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
              >
                Meet Our Team
              </Link>
            </div>

            {/* Right: quote card with portrait + caption combined */}
            {current ? (
            <div className="md:col-span-8">
              <div className="rounded-md bg-card p-6 shadow-md md:p-7">
                <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center md:gap-7">
                  <div>
                    <Quote className="h-7 w-7 text-gold" strokeWidth={1.8} />
                    <blockquote className="mt-3 text-base leading-relaxed text-foreground/90 md:text-lg">
                      {current.quote}
                    </blockquote>
                  </div>
                  <figure className="flex items-center gap-4 md:flex-col md:items-start md:gap-3 md:text-left">
                    <img
                      src={current.image}
                      alt={`${current.name}, ${current.role} at KS Lau & Co`}
                      loading="lazy"
                      className="h-20 w-20 rounded-full object-cover md:h-28 md:w-28"
                    />
                    <figcaption>
                      <p className="text-base font-semibold tracking-tight text-primary">
                        {current.name}
                      </p>
                      <p className="text-sm text-muted-foreground">{current.role}</p>
                    </figcaption>
                  </figure>
                </div>
              </div>

              {/* Dots below the entire quote section */}
              <div className="mt-5 flex items-center justify-center gap-2.5">
                {testimonials.map((t, i) => (
                  <button
                    key={t.id ?? `${t.name}-${i}`}
                    type="button"
                    onClick={() => setActiveTestimonial(i)}
                    aria-label={`Show testimonial from ${t.name}`}
                    aria-current={i === activeTestimonial ? "true" : undefined}
                    className={cn(
                      "h-2.5 w-2.5 rounded-full transition",
                      i === activeTestimonial
                        ? "bg-gold"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/60",
                    )}
                  />
                ))}
              </div>
            </div>
            ) : null}
          </div>
        </Container>
      </section>

      {/* Submit Your CV banner */}
      <section id="careers-cv" className="relative scroll-mt-28 isolate overflow-hidden">
        <img
          src={stockImages.aboutPenang}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/55"
        />
        <Container>
          <div className="flex flex-col gap-8 py-16 text-primary-foreground md:flex-row md:items-center md:justify-between md:py-20">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight md:text-3xl lg:text-4xl">
                Ready to Make an Impact?
              </h2>
              <p className="mt-3 max-w-xl text-lg leading-relaxed text-white/85">
                Join a team that values you, supports your growth and empowers you to
                build a better future.
              </p>
            </div>
            <a
              href={cvHref}
              className="inline-flex w-fit items-center justify-center gap-2 rounded-md border border-gold bg-transparent px-7 py-3.5 text-base font-semibold text-gold transition hover:bg-gold/10"
            >
              Submit Your CV <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}
